import React, { memo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import ModeTabsChooser from '../blocks/ModeTabsChooser';
import styled from 'styled-components';
import { Button, mainTheme } from '@plecto/ui';
import { useTranslation } from 'react-i18next';
import Preview from '../blocks/Preview';
const controlsHeight = '28px';

const StyledTimePeriodPopover = styled.div`
    font-size: 14px;
	line-height: 1.42857143;
	// A raised surface edged by the inset highlight, like the design system's menus.
	background-color: ${mainTheme.surface[3]};
	border-radius: 4px;
	box-shadow: ${mainTheme.shadow.raised}, ${mainTheme.shadow.inset};
	max-width: 400px;
	min-width: 400px;
	width: auto;
`;

const StyledPopoverPart = styled.div`
	padding: ${props => props.padded ? '10px' : '0'};
	height: auto;
	display: flex;
	flex-wrap: wrap;
	align-content: flex-start;
	color: ${mainTheme.text.primary};
	border-bottom: 1px solid ${mainTheme.border.decorative};

	.help-inline {
		max-height: none;
		margin-bottom: 0;
		min-height: 0;

		.icon {
			font-size: 16px;
			margin-left: 0;
		}
	}

	.react-number-input {
		box-sizing: border-box;
	}

	.react-number-input,
	.react-select-container .react-select__control {
		line-height: 1.42857143;
		height: ${controlsHeight};
		min-height: ${controlsHeight};
	}

	.react-select__value-container {
		padding: 0 10px;
		align-self: stretch;
		position: initial;
		height: 100%;
	}

	.react-select__indicators {
		height: 100%;
	}
`;

const StyledFooter = styled(StyledPopoverPart)`
	display: flex;
	align-items: center;
	gap: 8px;
	justify-content: space-between;
`;

const StyledContent = styled(StyledPopoverPart)`
	> *:not(:first-child) {
		margin-top: 1em;
	}

	> * {
		flex: 1 0 100%;
		display: flex;
		align-items: center;
		flex-wrap: wrap;

		&:not(.time-period-calendar) > * {
			margin-right: 0.5em;

			&:last-child {
				margin-right: 0;
			}
		}
	}
`;

const StyledActions = styled.div`
	flex: 0 0 auto;
	display: flex;
	gap: 8px;
`;
const TimePeriodPopoverTab = forwardRef(({
	children,
	state,
	onSave,
	onCancel,
	padded,
	previewOperationId,
	preview,
	changeMode,
	changeToPresetMode,
	clearPreset
}, ref) => {
	const { t } = useTranslation();
	return <StyledTimePeriodPopover ref={ref} className="time-period-tab">
		<ModeTabsChooser
			currentMode={state.mode}
			preset={state.preset}
			changeMode={changeMode}
			changeToPresetMode={changeToPresetMode}
			startMode={state.relativeStartDate.mode}
			clearPreset={clearPreset} />
		<StyledContent className="time-period-tab-content" padded={padded}>
			{children}
		</StyledContent>
		<StyledFooter className="time-period-tab-footer" padded>
			<Preview operationId={previewOperationId} preview={preview} />
			<StyledActions className='time-period-tab-footer-actions'>
				<Button
					variant="outline"
					className="cancel-button"
					onClick={onCancel}
					label={t('Cancel')}
				/>
				<Button
					variant="strong"
					className="save-button"
					onClick={onSave}
					label={t('Apply')}
				/>
			</StyledActions>
		</StyledFooter>
	</StyledTimePeriodPopover>;
});

TimePeriodPopoverTab.propTypes = {
	title: PropTypes.string,
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node
	]).isRequired,
	onSave: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	state: PropTypes.object.isRequired,
};

TimePeriodPopoverTab.displayName = 'TimePeriodPopoverTab';

export default memo(TimePeriodPopoverTab);
